/* eslint-disable jsx-a11y/alt-text */
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';

import BaseLink from '@/components/common/BaseLink/BaseLink';
import { screen } from '@/components/common/breakpoints';
import { Card } from '@/components/common/Card';
import { Image } from '@/components/common/image';
import { Heading, HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { blockUrlWithAudience } from '@/lib/utils';
import {
  getTextColour,
  isDarkBackground,
} from '@/sanity/schemas/objects/colours/colourUtils';
import { labelMedium } from '@/styles/typography';
import { transparentize } from '@/styles/utils';

import type { Audience, SanityImageAsset } from '@/sanity/sanity.types';
import type {
  SurfaceColourTokenType,
  SurfaceColourType,
  TextColourTokenType,
} from '@/types/colours';
import type { BlockWithAudience, TrackingTagType } from '@/types/shared';
import type { SanityReference } from '@/types/sanity';

export interface ResourceCardProps extends BlockWithAudience {
  _key?: string;
  _type?: 'resourceCard';
  image?: {
    _type: 'image';
    asset: SanityReference<SanityImageAsset>;
  };
  logo?: {
    _type: 'image';
    asset: SanityReference<SanityImageAsset>;
  };
  caption?: string;
  isCaptionUppercase?: boolean;
  heading: string;
  body?: string;
  trackingTag?: TrackingTagType;
  url: string;
  readingTime?: string;
  blogAuthorName?: string;
  blogAuthorLink?: string;
  backgroundColour?: SurfaceColourType;
  colourOverride?: SurfaceColourType;
  audienceRef?: Audience;
  noAudienceToggle?: boolean;
  authorImage?: {
    _type: 'image';
    asset: SanityReference<SanityImageAsset>;
  };
  blogPostPublishDate?: string;
}

const ResourceCard = ({
  blogAuthorName,
  authorImage,
  blogAuthorLink,
  blogPostPublishDate,
  image,
  logo,
  caption,
  isCaptionUppercase = false,
  heading,
  body,
  readingTime,
  trackingTag,
  url,
  backgroundColour,
  colourOverride,
  audienceRef,
  pageAudience,
  noAudienceToggle,
  isAudienceSwitcherEnabled,
}: ResourceCardProps) => {
  const { locale } = useRouter();
  const isImagePresent = !!image?.asset;

  const isBlog = !!blogAuthorName || !!blogAuthorLink;
  const cardLink = isBlog
    ? `/${locale}${url}`
    : blockUrlWithAudience(
      url,
      audienceRef,
      pageAudience,
      noAudienceToggle,
      isAudienceSwitcherEnabled,
    );

  let backgroundColourToken: SurfaceColourTokenType = '--surface-cool-strong';

  if (colourOverride) {
    backgroundColourToken = colourOverride.token;
  } else if (backgroundColour) {
    backgroundColourToken = backgroundColour.token;
  }

  return (
    <StyledCard
      className={
        trackingTag?.blockName &&
        `tracking-${trackingTag.blockName}__${trackingTag.blockAction}`
      }
      zoomOnHover
      borderRadius="m"
      padding={0}
      header={
        image?.asset && (
          <ImageContainerRow>
            <Image {...image} isDecorativeImage />
          </ImageContainerRow>
        )
      }
    >
      <HeadingLevel>
        <CardBodyWrapper
          isImagePresent={isImagePresent}
          backgroundColour={backgroundColourToken}
          bodyColour={getTextColour(backgroundColourToken)}
        >
          {logo?.asset && (
            <LogoWrapper>
              <Image {...logo} sizes="100px" />
            </LogoWrapper>
          )}
          <BaseLink
            href={cardLink}
            aria-label={`Link to ${heading} programme`}
            textColour="--text-strong"
          >
            {caption && (
              <Caption
                isCaptionUppercase={isCaptionUppercase}
                captionColour={getTextColour(backgroundColourToken, {
                  variation: 'base',
                })}
              >
                {caption}
              </Caption>
            )}

            <PostHeading
              size="heading-sm"
              cardHeadingColour={getTextColour(backgroundColourToken)}
            >
              {heading}
            </PostHeading>
          </BaseLink>
          {body && <Body size="body-md">{body}</Body>}
          {readingTime && (
            <ReadingTimeCaption>{readingTime}</ReadingTimeCaption>
          )}

          {blogAuthorName && (
            <BaseLineContainer>
              <AuthorImageContainer>
                {!!authorImage?.asset?._ref && (
                  <a
                    aria-label={`Link to ${blogAuthorName} page`}
                    href={`/${locale}${blogAuthorLink}`}
                  >
                    <Image
                      {...authorImage}
                      sizes="50vw"
                      alt={`Image of ${blogAuthorName}`}
                    />
                  </a>
                )}
              </AuthorImageContainer>
              <div>
                <AuthorCaption size="label-sm">
                  By {blogAuthorName}
                </AuthorCaption>
                <Paragraph size="body-sm">{blogPostPublishDate}</Paragraph>
              </div>
            </BaseLineContainer>
          )}
        </CardBodyWrapper>
      </HeadingLevel>
    </StyledCard>
  );
};

const sharedTransitionCss = css`
  transition:
    color 0.25s ease,
    background-color 0.25s ease-out,
    box-shadow 0.8s ease-out,
    fill 0.25s ease;
`;

const StyledCard = styled(Card)`
  overflow: hidden;
  width: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.25s ease-out;

  :hover {
    box-shadow: var(--shadow-subtle);
  }
`;

const Body = styled(Paragraph)`
  z-index: 1;
`;

const ImageContainerRow = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  height: 236px;
  width: calc(100% + 1px);
  flex-direction: column;
  border-radius: calc(var(--radius-50) / 2) calc(var(--radius-50) / 2) 0 0;
  img {
    object-fit: cover;
    border-radius: calc(var(--radius-50) / 2) calc(var(--radius-50) / 2) 0 0;
    height: 100%;
  }
`;

const LogoWrapper = styled.div`
  margin-bottom: var(--spacing-xxx-small);
  ${screen.sm} {
    margin-bottom: var(--spacing-xx-small);
  }
  > img {
    max-height: 55px;
    max-width: 120px;
    min-width: unset;
    width: auto;
    overflow: hidden;
    margin-left: 0;
  }
`;

const PostHeading = styled(Heading) <{ cardHeadingColour: TextColourTokenType }>`
  z-index: 1;

  padding-bottom: var(--spacing-xxx-small);
  color: ${({ cardHeadingColour }) => `var(${cardHeadingColour})`};
`;

const CardBodyWrapper = styled.div<{
  backgroundColour?: SurfaceColourTokenType;
  bodyColour?: TextColourTokenType;
  isImagePresent: boolean;
}>`
  background-color: ${(p) => `var(${p.backgroundColour})`};
  color: ${({ bodyColour }) => `var(${bodyColour})`};

  padding: ${({ isImagePresent }) =>
    isImagePresent
      ? `var(--spacing-x-small) var(--spacing-250) var(--spacing-250) var(--spacing-250)`
      : `var(--spacing-small) var(--spacing-900) var(--spacing-small) var(--spacing-small)`};

  ${screen.sm} {
    padding: ${({ isImagePresent }) =>
    isImagePresent
      ? `var(--spacing-small) var(--spacing-250) var(--spacing-250) var(--spacing-250)`
      : `var(--spacing-250) var(--spacing-900) var(--spacing-250) var(--spacing-250)`};
  }

  height: 100%;
  display: flex;
  flex-direction: column;
  ${sharedTransitionCss};

  ${Paragraph} {
    z-index: 1;
  }

  ${StyledCard}:hover & {
    ${sharedTransitionCss};
    border-radius: ${({ isImagePresent }) => (isImagePresent ? '0px' : '8px')};
  }

  ${Body} {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
  }
  p {
    -webkit-line-clamp: 3;
    ${sharedTransitionCss};
  }

  h2 {
    ${sharedTransitionCss};
  }

  ${screen.md} {
    &:hover {
      background-color: ${(p) =>
    isDarkBackground(p.backgroundColour)
      ? `${transparentize('var(--surface-inverse-primary)', 96)}`
      : `${transparentize('var(--surface-primary-subtle)', 60)}`};
    }
    p {
      -webkit-line-clamp: 4;
    }
  }
`;

const Caption = styled.p<{
  captionColour?: TextColourTokenType;
  isCaptionUppercase?: boolean;
}>`
  ${labelMedium}
  z-index:1;
  display: inline-block;
  padding-bottom: var(--spacing-xxx-small);
  color: ${({ captionColour }) => `var(${captionColour})`};
  text-transform: ${({ isCaptionUppercase }) =>
    isCaptionUppercase ? 'uppercase' : 'none'};

  ${StyledCard}:hover & {
    color: ${({ captionColour }) => `var(${captionColour})`};
  }
`;

const ReadingTimeCaption = styled.p`
  ${labelMedium}
  font-size: 14px;
  display: inline-block;
  margin-top: var(--spacing-xx-small);
`;

const BaseLineContainer = styled.div`
  display: flex;
  align-items: center;
  padding-top: var(--spacing-small);
  gap: var(--spacing-xx-small);
  z-index: 4;
  cursor: auto;
`;

const AuthorImageContainer = styled.div`
  img {
    border-radius: 50%;
    width: 40px;
    height: 40px;
  }
  a {
    display: block;
  }

  transition: transform 0.4s ease-out;
  &:hover {
    transform: scale(1.03);
  }
`;

const AuthorCaption = styled(Paragraph)`
  margin-bottom: 2px;
`;

export { ResourceCard };
